import { deriveCapsuleItems } from '@/trendData/trendCapsule.utilities';
import { getCertainId } from '@/utilities/id.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  CapsuleTiming,
  StitchBreak,
  StitchDetailsResult,
  StitchTime,
} from '@/trendData/trendCapsuleChart.utilities';
import type {
  CapsuleCalculatedChartItem,
  CapsuleChartItem,
  CapsuleRuntimeItem,
  CapsuleToAdd,
  PreviewChartItem,
} from '@/trendData/trendData.types';

/**
 * In-memory store for the capsule dataset. Everything here is runtime-only and must never be
 * persisted; the persisted capsule state (selection, editing id, preview definitions, property
 * colors) lives in the flux capsule store. Items here carry no `selected`; consumers merge the
 * selection in at read time.
 */

export interface CapsuleRuntimeState {
  /** The capsule dataset shown in the capsules pane, without selection state */
  items: CapsuleRuntimeItem[];
  /** The rows the chart draws, derived by `deriveChartItems` */
  chartItems: CapsuleChartItem[];
  /** The per-condition capsule results the chart rows are derived from */
  calculatedChartItems: CapsuleCalculatedChartItem[];
  /** The preview rows shown while a condition tool is open */
  previewChartItem: PreviewChartItem;
  /** Chain-view gaps between capsules, in the format Highcharts expects */
  stitchBreaks: StitchBreak[];
  /** Chain-view visible time periods */
  stitchTimes: StitchTime[];
  /** Chain-view per-capsule timings */
  capsulesTimings: CapsuleTiming[];
  /** True once chain-view stitch details have been computed for the current capsules */
  stitchDetailsSet: boolean;
}

export interface CapsuleRuntimeActions {
  /** Derives and records the capsule items from fetched capsules, so no caller can store raw ones */
  setCapsules(capsules: CapsuleToAdd[]): void;
  /** Discards the items matching the given ids, for when their conditions leave the display */
  removeItems(ids: string[]): void;
  setChartItems(chartItems: CapsuleChartItem[]): void;
  setCalculatedChartItems(conditions: CapsuleCalculatedChartItem[]): void;
  /** Merges conditions into the calculated chart items, replacing same-id entries */
  addCalculatedChartItems(conditions: CapsuleCalculatedChartItem[]): void;
  setPreviewChartItem(previewChartItem: PreviewChartItem): void;
  setStitchBreaks(stitchBreaks: StitchBreak[]): void;
  setStitchTimes(stitchTimes: StitchTime[]): void;
  /** Syncs a condition's selection onto its calculated chart items */
  setCalculatedChartItemSelected(id: string, selected: boolean): void;
  /** Applies lane, thickness, overlay, and order customizations to a condition's chart and calculated chart items */
  customizeChartItems(payload: {
    id: string;
    lineWidth?: number;
    lane?: number;
    overlay?: number;
    orderInLane?: number;
  }): void;
  setItemColors(ids: string[], color: string | undefined): void;
  /** Recolors the chart rows and calculated items belonging to a condition */
  setChartColorsForCondition(conditionId: string, color: string | undefined): void;
  mergePreviewColor(color: string | undefined): void;
  /** Merges properties into the matching items in one pass; a nil value deletes the property */
  mergeItemProps(ids: string[], props: Record<string, unknown>): void;
  /** Merges properties into the preview chart item; a nil value deletes the property */
  mergePreviewChartItemProps(props: Record<string, unknown>): void;
  /**
   * Applies a stitch-details derivation: no stitch times clears everything, too many breaks keeps
   * the previous breaks, and only a full result marks the details as set.
   */
  applyStitchDetails(details: StitchDetailsResult): void;
  /** Marks the stitch details stale without discarding them, e.g. while capsules are loading */
  setStitchDetailsSet(stitchDetailsSet: boolean): void;
  reset(): void;
}

export const initialState: CapsuleRuntimeState = Object.freeze({
  items: [],
  chartItems: [],
  calculatedChartItems: [],
  previewChartItem: {},
  stitchBreaks: [],
  stitchTimes: [],
  capsulesTimings: [],
  stitchDetailsSet: false,
});

const STORE_NAME = 'CapsuleRuntime';

/** Merges properties into a draft object; a nil value deletes the property */
const mergeProps = (target: Record<string, unknown>, props: Record<string, unknown>) => {
  Object.entries(props).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      delete target[key];
    } else {
      target[key] = value;
    }
  });
};

/** Whether two derivations hold the same flat entries in the same order */
const sameEntries = (previous: readonly Record<string, unknown>[], next: readonly Record<string, unknown>[]) =>
  previous.length === next.length &&
  previous.every((entry, index) => {
    const other = next[index];
    const keys = Object.keys(entry);
    return keys.length === Object.keys(other).length && keys.every((key) => entry[key] === other[key]);
  });

const capsuleRuntimeActionsFactory: ActionsFactory<CapsuleRuntimeState, CapsuleRuntimeActions> = (
  set,
  get,
  resetState,
) => ({
  setCapsules: (capsules) => {
    set((draft) => {
      draft.items = deriveCapsuleItems(capsules);
    });
  },

  removeItems: (ids) => {
    if (!get().items.some((item) => ids.includes(item.id))) {
      return;
    }
    set((draft) => {
      draft.items = draft.items.filter((item) => !ids.includes(item.id));
    });
  },

  setChartItems: (chartItems) => {
    set((draft) => {
      draft.chartItems = chartItems;
    });
  },

  setCalculatedChartItems: (conditions) => {
    set((draft) => {
      draft.calculatedChartItems = conditions;
    });
  },

  addCalculatedChartItems: (conditions) => {
    set((draft) => {
      const seen = new Set<string>();
      draft.calculatedChartItems = [...conditions, ...draft.calculatedChartItems].filter((item) => {
        if (seen.has(item.id)) {
          return false;
        }
        seen.add(item.id);
        return true;
      });
    });
  },

  setPreviewChartItem: (previewChartItem) => {
    set((draft) => {
      draft.previewChartItem = previewChartItem;
    });
  },

  setStitchBreaks: (stitchBreaks) => {
    set((draft) => {
      draft.stitchBreaks = stitchBreaks;
    });
  },

  setStitchTimes: (stitchTimes) => {
    set((draft) => {
      draft.stitchTimes = stitchTimes;
    });
  },

  setCalculatedChartItemSelected: (id, selected) => {
    if (!get().calculatedChartItems.some((item) => item.id === id && item.selected !== selected)) {
      return;
    }
    set((draft) => {
      draft.calculatedChartItems.forEach((item) => {
        if (item.id === id) {
          item.selected = selected;
        }
      });
    });
  },

  customizeChartItems: (payload) => {
    const { id, lane, lineWidth, overlay, orderInLane } = payload;
    const hasLane = 'lane' in payload;
    const hasLineWidth = 'lineWidth' in payload;
    const hasOverlay = 'overlay' in payload;
    const hasOrderInLane = 'orderInLane' in payload;
    set((draft) => {
      const applyTo = (chartItem: { lane?: number; thickness?: number; overlay?: number; orderInLane?: number }) => {
        if (hasLane) {
          chartItem.lane = lane;
        }
        if (hasLineWidth) {
          chartItem.thickness = lineWidth;
        }
        if (hasOverlay) {
          chartItem.overlay = overlay;
        }
        if (hasOrderInLane) {
          chartItem.orderInLane = orderInLane;
        }
      };
      draft.chartItems.forEach((chartItem) => {
        if (chartItem.conditionId === id || getCertainId(chartItem.conditionId) === id) {
          applyTo(chartItem);
        }
      });
      draft.calculatedChartItems.forEach((chartItem) => {
        if (chartItem.id === id || getCertainId(chartItem.id) === id) {
          applyTo(chartItem);
        }
      });
    });
  },

  setItemColors: (ids, color) => {
    const idSet = new Set(ids);
    set((draft) => {
      draft.items.forEach((item) => {
        if (idSet.has(item.id)) {
          item.color = color as string;
        }
      });
    });
  },

  setChartColorsForCondition: (conditionId, color) => {
    set((draft) => {
      draft.chartItems.forEach((chartItem) => {
        if (chartItem.conditionId === conditionId) {
          chartItem.color = color as string;
        }
      });
      draft.calculatedChartItems.forEach((chartItem) => {
        if (chartItem.id === conditionId) {
          chartItem.color = color as string;
        }
      });
    });
  },

  mergePreviewChartItemProps: (props) => {
    set((draft) => {
      mergeProps(draft.previewChartItem as Record<string, unknown>, props);
    });
  },

  mergePreviewColor: (color) => {
    set((draft) => {
      // The preview is an object for one condition or an array of lanes; on the array this hangs
      // an unused property off the array itself, since the lanes keep their own colors
      (draft.previewChartItem as { color?: string }).color = color;
    });
  },

  mergeItemProps: (ids, props) => {
    const idSet = new Set(ids);
    if (!get().items.some((item) => idSet.has(item.id))) {
      return;
    }
    set((draft) => {
      draft.items.forEach((candidate) => {
        if (!idSet.has(candidate.id)) {
          return;
        }
        mergeProps(candidate as Record<string, unknown>, props);
      });
    });
  },

  applyStitchDetails: (details) => {
    // Unchanged derivations keep their references so chart subscribers do not redraw
    const assignIfChanged = <K extends 'stitchTimes' | 'capsulesTimings' | 'stitchBreaks'>(
      draft: CapsuleRuntimeState,
      key: K,
      value: CapsuleRuntimeState[K],
    ) => {
      if (!sameEntries(draft[key], value)) {
        draft[key] = value;
      }
    };
    set((draft) => {
      if (details.stitchTimes.length === 0) {
        assignIfChanged(draft, 'stitchTimes', []);
        assignIfChanged(draft, 'capsulesTimings', []);
        assignIfChanged(draft, 'stitchBreaks', []);
        draft.stitchDetailsSet = false;
        return;
      }

      assignIfChanged(draft, 'stitchTimes', details.stitchTimes);
      assignIfChanged(draft, 'capsulesTimings', details.capsulesTimings);
      if (details.tooManyBreaks) {
        draft.stitchDetailsSet = false;
        return;
      }

      assignIfChanged(draft, 'stitchBreaks', details.stitchBreaks);
      draft.stitchDetailsSet = true;
    });
  },

  setStitchDetailsSet: (stitchDetailsSet) => {
    set((draft) => {
      draft.stitchDetailsSet = stitchDetailsSet;
    });
  },

  reset: resetState,
});

const [CapsuleRuntimeStoreProvider, useCapsuleRuntimeStoreInstance, useCapsuleRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  capsuleRuntimeActionsFactory,
);

export { CapsuleRuntimeStoreProvider };

export const useCapsuleRuntimeItems = () => useCapsuleRuntimeSelector((state) => state.items);

export const useCapsuleRuntimeChartItems = () => useCapsuleRuntimeSelector((state) => state.chartItems);

export const useCapsuleRuntimeStitchBreaks = () => useCapsuleRuntimeSelector((state) => state.stitchBreaks);

export const useCapsuleRuntimePreviewChartItem = () => useCapsuleRuntimeSelector((state) => state.previewChartItem);

export const useCapsuleRuntimeActions = () => useCapsuleRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateCapsuleRuntimeStore(initialStateOverrides?: Partial<CapsuleRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    capsuleRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
